import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CustomComponent } from '../../../models/CustomComponent.model';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Heading,
  Link,
  List,
  Indent,
  type EditorConfig,
} from 'ckeditor5';
import { SurveyCreatorModule } from 'survey-creator-angular';
import {
  SurveyCreatorModel,
  PropertyGridEditorCollection,
  editorLocalization,
} from 'survey-creator-core';
import { Serializer, Base, Question, surveyLocalization } from 'survey-core';
import { ComponentLibraryService } from '../../../services/component-library/component-library.service';
import { MaterialComponent } from '../../../interfaces/iMaterialComponent';
import { FunctionRegistryService } from '../../../services/function-registry/function-registry.service';
import { Subject, takeUntil } from 'rxjs';
import {
  CdkDragDrop,
  CdkDrag,
  CdkDropList,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { AuthService } from '../../../services/auth/auth.service';
import { translationsPanel } from '../../../surveyJS-custom-questions/custom-tab-panel';
import { dualPanelSelector } from '../../../surveyJS-custom-questions/dual-panel-selector';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ComponentPermissionService } from '../../../services/component-permission/component-permission.service';
import { Observable } from 'rxjs';
import { dynamicTableQuestion } from '../../../surveyJS-custom-questions/dynamic-table-question';
import type {
  TabSetCondition,
  TabsConfigRules,
  AlwaysVisibleTabConfig,
} from '../../dynamic-tabs/dynamic-tabs.component';
import { registerRngSurveyVisibilityDataSourceProperties } from '../../../surveyJS-custom-questions/rng-survey-serializer';
import {
  EditModalNativeSelectComponent,
  NativeSelectOption,
} from './edit-modal-native-select.component';
import {
  BUNDLED_SURVEY_FORM_SOURCE,
  getBundledSurveyFormIdFromComponentInputs,
  resolveBundledSurveyForm,
} from '../../../forms/bundled-survey-forms';

/** Local type for a single tab in the editor (same shape as TabConfig). */
interface TabConfigEdit {
  text: string;
  slug: string;
}

interface AlwaysVisibleTabEdit {
  text: string;
  slug: string;
  dataSource: string;
}

/** Local type for a rule in the tab set editor. */
interface TabSetRuleEdit {
  condition: TabSetCondition;
  tabs: TabConfigEdit[];
  isDefault?: boolean;
}

surveyLocalization.defaultLocale = 'da';

@Component({
  selector: 'app-edit-component-modal',
  standalone: true,
  imports: [
    CKEditorModule,
    SurveyCreatorModule,
    CdkDrag,
    CdkDropList,
    EditModalNativeSelectComponent,
  ],
  templateUrl: './edit-component-modal.component.html',
  styleUrls: [
    './edit-component-modal.component.scss',
    '../../../surveyJS-custom-questions/custom-tab-panel.scss',
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class EditComponentModalComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() isOpen: boolean = false;
  @Input() component?: CustomComponent;
  @Output() saveChanges = new EventEmitter<CustomComponent>();
  @Output() closeModal = new EventEmitter<void>();

  public isLayoutReady = false;
  public Editor = ClassicEditor;
  public editorConfig = this.createEditorConfig();
  private jsonValidationErrors = new Map<string, boolean>();
  public surveyCreator!: SurveyCreatorModel;
  availableComponents: MaterialComponent[] = [];
  selectedComponent: string = '';
  componentProperties: { [key: string]: string } = {};
  private editor: any;

  @ViewChild('editorElement') editorElement?: ElementRef;

  private destroy$ = new Subject<void>();

  selectedCssShortcut: string = '';

  get codeBundledSurveyFormId():
    | ReturnType<typeof getBundledSurveyFormIdFromComponentInputs>
    | null {
    return getBundledSurveyFormIdFromComponentInputs(this.component?.inputs);
  }

  get codeBundledSurveyFormSourcePath(): string | null {
    const id = this.codeBundledSurveyFormId;
    return id ? BUNDLED_SURVEY_FORM_SOURCE[id] : null;
  }

  availableRoles: string[] = [
    'superadmin',
    'orgadmin',
    'orgbruger',
    'bruger',
    'uddansvarlig',
    'uddassistent',
    'authenticated',
    'unauthenticated',
  ];
  selectedRoles: string[] = [];

  // Permission-related properties
  availablePermissions: string[] = [];
  selectedPermissionFunction: string = '';
  permissionContextFields: string = '';
  enablePermissionCheck: boolean = false;

  // Example text for help documentation
  readonly permissionExamples = {
    modalData: '{{modalData.eventId}}',
    currentUser: '{{currentUser.id}}',
    routeParams: '{{route.params.id}}',
  };

  // Dynamic Tabs: tab set rules (conditional menus) editor
  tabSetRules: TabSetRuleEdit[] = [];
  alwaysVisibleTabs: AlwaysVisibleTabEdit[] = [];
  readonly tabSetAuthOptions: {
    value: TabSetCondition['auth'];
    label: string;
  }[] = [
    { value: 'any', label: 'Anyone' },
    { value: 'logged-in', label: 'Only logged in' },
    { value: 'not-logged-in', label: 'Only not logged in' },
    { value: 'data-source-true', label: 'When data source is true' },
    { value: 'data-source-false', label: 'When data source is false' },
  ];
  readonly cssShortcutOptions: NativeSelectOption[] = [
    { value: '', label: 'Select a CSS shortcut...' },
    { value: 'grid-column: span 4;', label: 'Span 4 columns' },
    { value: 'grid-column: span 6;', label: 'Span 6 columns' },
    { value: 'grid-column: span 8;', label: 'Span 8 columns' },
    { value: 'grid-column: 5 / span 8;', label: 'Start at column 5, span 8' },
    { value: 'grid-column: 7 / span 6;', label: 'Start at column 7, span 6' },
    { value: 'grid-row: span 2;', label: 'Span 2 rows' },
    { value: 'grid-row: span 3;', label: 'Span 3 rows' },
    { value: 'grid-row: span 4;', label: 'Span 4 rows' },
    { value: 'grid-row: 2 / span 1;', label: 'Start at row 2' },
    { value: 'text-align: right;', label: 'Align text right' },
  ];
  readonly sortDirectionOptions: NativeSelectOption[] = [
    { value: 'asc', label: 'Ascending (A-Z, 1-9, oldest first)' },
    { value: 'desc', label: 'Descending (Z-A, 9-1, newest first)' },
  ];
  tabSetDataSources: string[] = [];

  /** Roles available for tab set rules (who sees these tabs). */
  readonly tabSetAvailableRoles: string[] = [
    'superadmin',
    'orgadmin',
    'orgbruger',
    'uddansvarlig',
    'uddassistent',
    'bruger',
    'primaer',
    'ekstern',
  ];

  constructor(
    private changeDetector: ChangeDetectorRef,
    private componentLibrary: ComponentLibraryService,
    private functionRegistry: FunctionRegistryService,
    private authService: AuthService,
    private componentDataSourceService: ComponentDataSourceService,
    private componentPermissionService: ComponentPermissionService
  ) {
    this.availableComponents = this.componentLibrary.getComponents();
    this.tabSetDataSources = this.componentDataSourceService
      .getRegisteredDataSourceKeys()
      .sort();
  }

  public sanitizer = inject(DomSanitizer);

  private createEditorConfig(): EditorConfig {
    return {
      licenseKey: 'GPL',
      plugins: [
        Essentials,
        Paragraph,
        Bold,
        Italic,
        Underline,
        Heading,
        Link,
        List,
        Indent,
      ],
      toolbar: {
        items: [
          'heading',
          '|',
          'bold',
          'italic',
          'underline',
          '|',
          'link',
          'bulletedList',
          'numberedList',
          '|',
          'outdent',
          'indent',
          '|',
          'undo',
          'redo',
        ],
      },
      heading: {
        options: [
          {
            model: 'paragraph' as const,
            title: 'Brødtekst',
            class: 'ck-heading_paragraph',
          },
          {
            model: 'heading1' as const,
            view: 'h1',
            title: 'Heading 1',
            class: 'ck-heading_heading1',
          },
          {
            model: 'heading2' as const,
            view: 'h2',
            title: 'Heading 2',
            class: 'ck-heading_heading2',
          },
          {
            model: 'heading3' as const,
            view: 'h3',
            title: 'Heading 3',
            class: 'ck-heading_heading3',
          },
        ],
      },
    };
  }

  public ngAfterViewInit(): void {
    this.editorConfig = this.createEditorConfig();

    this.isLayoutReady = true;
    this.changeDetector.detectChanges();
  }

  save() {

    // Ensure all required settings exist before saving
    if (this.component && this.component.name === 'DynamicTableComponent') {
      // Initialize sort settings if they don't exist
      if (!this.component.settings['defaultSortColumn']) {
        this.component.settings['defaultSortColumn'] = {
          type: 'text',
          value: '',
        };
      }

      if (!this.component.settings['defaultSortDirection']) {
        this.component.settings['defaultSortDirection'] = {
          type: 'select',
          value: 'asc',
        };
      }

      // Ensure the values are properly set (not undefined or null)
      if (
        this.component.settings['defaultSortColumn'].value === undefined ||
        this.component.settings['defaultSortColumn'].value === null
      ) {
        this.component.settings['defaultSortColumn'].value = '';
      }

      if (
        this.component.settings['defaultSortDirection'].value === undefined ||
        this.component.settings['defaultSortDirection'].value === null ||
        !['asc', 'desc'].includes(
          this.component.settings['defaultSortDirection'].value
        )
      ) {
        this.component.settings['defaultSortDirection'].value = 'asc';
      }

      // Log sort settings for debugging
    }

    // Set allowed roles for the component
    if (this.component) {
      this.component.settings['allowedRoles'] = {
        type: 'roles',
        value: this.selectedRoles,
      };

      // Clear the old invisibleRoles setting since we're now using allowedRoles
      if (this.component.settings['invisibleRoles']) {
        delete this.component.settings['invisibleRoles'];
      }

      // Update permission settings
      this.component.settings['permissionFunction'] = {
        type: 'text',
        value: this.selectedPermissionFunction,
      };
      this.component.settings['permissionContextFields'] = {
        type: 'text',
        value: this.permissionContextFields,
      };
      this.component.settings['enablePermissionCheck'] = {
        type: 'boolean',
        value: this.enablePermissionCheck,
      };

      // Save form-level data source properties if this is a survey component
      if (
        this.component.name === 'dynamic-form' &&
        this.surveyCreator &&
        !this.codeBundledSurveyFormId
      ) {
        const survey = this.surveyCreator.survey;
        if (survey) {
          // Get the data source properties from the survey
          const dataSource = survey.getPropertyValue('dataSource');
          const dataSourceKeyField =
            survey.getPropertyValue('dataSourceKeyField');
          const dataSourceValueField = survey.getPropertyValue(
            'dataSourceValueField'
          );
          const showEmptyFormSubmit = survey.getPropertyValue(
            'showEmptyFormSubmit'
          );

          // Check if this is an event edit form
          const isEventEditForm =
            this.component.inputs['surveyJSON']?.value &&
            typeof this.component.inputs['surveyJSON'].value === 'string' &&
            this.component.inputs['surveyJSON'].value.includes(
              '"title": "Rediger Event"'
            );

          // Store them in the component settings
          if (dataSource || isEventEditForm) {
            this.component.settings['formDataSource'] = {
              type: 'string',
              value: isEventEditForm ? 'event_form_data' : dataSource,
            };
          }

          if (dataSourceKeyField) {
            this.component.settings['formDataSourceKeyField'] = {
              type: 'string',
              value: dataSourceKeyField,
            };
          }

          if (dataSourceValueField) {
            this.component.settings['formDataSourceValueField'] = {
              type: 'string',
              value: dataSourceValueField,
            };
          }

          // Persist showEmptyFormSubmit explicitly so it is not stripped when false
          if (this.component.inputs['surveyJSON']) {
            const persisted = this.persistShowEmptyFormSubmit(
              this.component.inputs['surveyJSON'].value,
              showEmptyFormSubmit
            );
            this.component.inputs['surveyJSON'].value = persisted;
            this.surveyCreator.text = persisted;
          }
        }
      }

      // Ensure customCSS is properly formatted
      if (this.component.customCSS) {
        if (typeof this.component.customCSS === 'string') {
          const trimmedCSS = this.component.customCSS.trim();
          if (trimmedCSS && !trimmedCSS.endsWith(';')) {
            this.component.customCSS = trimmedCSS + ';';
          }
        } else if (
          typeof this.component.customCSS === 'object' &&
          this.component.customCSS !== null
        ) {
          const cssString = Object.entries(this.component.customCSS)
            .map(([property, value]) => `${property}: ${value};`)
            .join(' ');
          this.component.customCSS = cssString;
        }
      }

      // Save all changes

      // Add 'settings' to the component inputs to ensure they're accessible
      if (!this.component.inputs['settings']) {
        this.component.inputs['settings'] = {
          type: 'object',
          value: this.component.settings,
        };
      } else {
        this.component.inputs['settings'].value = this.component.settings;
      }

      // Dynamic Tabs: persist tab set rules
      if (
        this.component.name === 'DynamicTabsComponent' &&
        this.component.inputs['tabsConfig']
      ) {
        this.component.inputs['tabsConfig'].value =
          this.serializeRulesToTabsConfig(
            this.tabSetRules,
            this.alwaysVisibleTabs
          );
        this.component.inputs['tabsConfig'].type = 'object';
      }

      // Send the updated component to the parent
      this.saveChanges.emit(this.component);
      this.close();
    }
  }

  close() {
    this.closeModal.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    // When component input changes, initialize its settings
    if (changes['component']) {
      // Initialize immediately, even if component is null (it might be set later)
      if (this.component) {
        this.initializeComponentSettings();

        // Use setTimeout to ensure change detection runs after initialization
        setTimeout(() => {
          this.changeDetector.detectChanges();
        }, 0);
      } else {
      }

      // Also re-initialize other component-specific data
      if (this.component?.settings['allowedRoles']?.value) {
        this.selectedRoles = [...this.component.settings['allowedRoles'].value];
        this.availableRoles = this.availableRoles.filter(
          (role) => !this.selectedRoles.includes(role)
        );
      } else {
        this.selectedRoles = [];
        this.availableRoles = [
          'superadmin',
          'orgadmin',
          'orgbruger',
          'bruger',
          'uddansvarlig',
          'uddassistent',
          'authenticated',
          'unauthenticated',
        ];
      }

      // Re-initialize permission settings
      if (this.component?.settings['permissionFunction']?.value) {
        this.selectedPermissionFunction =
          this.component.settings['permissionFunction'].value;
      } else {
        this.selectedPermissionFunction = '';
      }
      if (this.component?.settings['permissionContextFields']?.value) {
        this.permissionContextFields =
          this.component.settings['permissionContextFields'].value;
      } else {
        this.permissionContextFields = '';
      }
      if (
        this.component?.settings['enablePermissionCheck']?.value !== undefined
      ) {
        this.enablePermissionCheck =
          this.component.settings['enablePermissionCheck'].value;
      } else {
        this.enablePermissionCheck = false;
      }

      // Dynamic Tabs: load tab set rules into editor
      if (this.component?.name === 'DynamicTabsComponent') {
        this.ensureTabSetRulesFromComponent();
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    // No need to destroy CKEditor instances as they are managed by Angular
  }

  onEditorReady(editor: any) {
    this.editor = editor;
  }

  onCkEditorChange(
    input: { value: { value: any } },
    event: { editor?: { getData(): string } }
  ): void {
    input.value.value = event.editor?.getData() ?? '';
  }

  validateJSON(
    input: { key: string; value: { type: string; value: any } },
    value?: string
  ): void {
    try {
      if (value) {
        JSON.parse(value);
        this.jsonValidationErrors.set(input.key, false);
      }
    } catch (e) {
      this.jsonValidationErrors.set(input.key, true);
    }
  }

  isJsonInvalid(key: string): boolean {
    return this.jsonValidationErrors.get(key) || false;
  }

  ngOnInit() {

    // Initialize component-specific settings if component is already available
    if (this.component) {
      this.initializeComponentSettings();
      if (this.component.name === 'DynamicTabsComponent') {
        this.ensureTabSetRulesFromComponent();
      }
      // Use setTimeout to ensure change detection runs after initialization
      setTimeout(() => {
        this.changeDetector.detectChanges();
      }, 0);
    }

    const creatorOptions = {
      showLogicTab: true,
      showTranslationTab: true,
      showThemeTab: true,
      isAutoSave: true,
      showPreviewTab: true,
      showJSONEditorTab: true,
      haveCommercialLicense: true,
    };

    registerRngSurveyVisibilityDataSourceProperties(
      this.componentDataSourceService['registeredDataSources']
    );

    this.surveyCreator = new SurveyCreatorModel(creatorOptions);
    this.surveyCreator.locale = 'da';

    // Register a custom property editor for authVisibility
    PropertyGridEditorCollection.register({
      fit: (prop) => {
        return prop.name === 'authVisibility';
      },
      getJSON: (obj, prop, options) => {
        return {
          type: 'dropdown',
          choices: [
            { value: 0, text: 'Everyone' },
            { value: 1, text: 'Only logged in' },
            { value: 2, text: 'NOT logged in' },
            { value: 3, text: 'Nobody' },
          ],
          renderAs: 'select',
          title: 'Who can see this page?',
        };
      },
    });

    // Register translations for RNG category
    const translations = editorLocalization.getLocale('');
    if (!translations.pe.rng) {
      translations.pe.rng = 'RNG Settings';
    }

    // Add Danish translation if needed
    const dkTranslations = editorLocalization.getLocale('da');
    if (!dkTranslations.pe.rng) {
      dkTranslations.pe.rng = 'RNG Indstillinger';
    }

    // Add dataSource property to all questions
    Serializer.addProperty('question', {
      name: 'dataSource',
      type: 'string',
      category: 'RNG Settings',
      visibleIndex: 0,
      choices: this.componentDataSourceService['registeredDataSources'],
      default: '',
    });

    // Add dataSourceKeyField property to all questions
    Serializer.addProperty('question', {
      name: 'dataSourceKeyField',
      type: 'string',
      category: 'RNG Settings',
      visibleIndex: 1,
      default: '',
      visibleIf: (obj) => obj.dataSource !== undefined && obj.dataSource !== '',
    });

    // Add dataSourceValueField property to all questions
    Serializer.addProperty('question', {
      name: 'dataSourceValueField',
      type: 'string',
      category: 'RNG Settings',
      visibleIndex: 2,
      default: '',
      visibleIf: (obj) => obj.dataSource !== undefined && obj.dataSource !== '',
    });

    // Add dataSource property to the survey (form) level
    Serializer.addProperty('survey', {
      name: 'dataSource',
      type: 'string',
      category: 'RNG Settings',
      visibleIndex: 0,
      choices: this.componentDataSourceService['registeredDataSources'],
      default: '',
    });

    // Explicitly register the RNG Settings category for the survey object
    const surveyProperties = Serializer.getPropertiesByObj({
      getType: () => 'survey',
    });
    const dataSourceProp = surveyProperties.find(
      (p) => p.name === 'dataSource'
    );
    if (dataSourceProp) {
      dataSourceProp.category = 'RNG Settings';
    }

    // Add dataSourceKeyField property to the survey (form) level
    Serializer.addProperty('survey', {
      name: 'dataSourceKeyField',
      type: 'string',
      category: 'RNG Settings',
      visibleIndex: 1,
      default: '',
      visibleIf: (obj) => obj.dataSource !== undefined && obj.dataSource !== '',
    });

    // Add dataSourceValueField property to the survey (form) level
    Serializer.addProperty('survey', {
      name: 'dataSourceValueField',
      type: 'string',
      category: 'RNG Settings',
      visibleIndex: 2,
      default: '',
      visibleIf: (obj) => obj.dataSource !== undefined && obj.dataSource !== '',
    });

    // Add empty form submit properties to the survey level
    Serializer.addProperty('survey', {
      name: 'showEmptyFormSubmit',
      displayName: 'Show Submit Button for Empty Forms',
      type: 'boolean',
      category: 'RNG Settings',
      visibleIndex: 10,
      // Use null as default so SurveyJS doesn't strip the property when true
      default: null,
    });

    // Make sure the survey-level properties are visible in the property grid
    this.surveyCreator.onShowPropertyGridVisiblityChanged.add(
      (_: any, options: any) => {
        // For survey-level properties, check if the selected object is a survey
        if (
          (options.property.name === 'dataSource' ||
            options.property.name === 'dataSourceKeyField' ||
            options.property.name === 'dataSourceValueField' ||
            options.property.name === 'showEmptyFormSubmit') &&
          options.obj.getType &&
          options.obj.getType() === 'survey'
        ) {
          options.visible = true;
        }

        // For question-level properties
        if (
          options.property.name === 'authVisibility' ||
          options.property.name === 'visibilityDataSource' ||
          (options.property.name === 'rendersAs' &&
            options.obj?.getType() === 'dropdown')
        ) {
          options.visible = true;
        }
      }
    );

    // Add mergeWithStaticChoices property
    Serializer.addProperty('question', {
      name: 'mergeWithStaticChoices',
      type: 'boolean',
      category: 'RNG Settings',
      visibleIndex: 3,
      default: false,
      visibleIf: (obj) =>
        obj.getType() === 'dropdown' &&
        obj.dataSource !== undefined &&
        obj.dataSource !== '',
    });

    // Add allowCreate property (e.g. customer_guid dropdown in add/edit-event)
    Serializer.addProperty('question', {
      name: 'allowCreate',
      type: 'boolean',
      category: 'RNG Settings',
      visibleIndex: 3,
      default: false,
      visibleIf: (obj) =>
        obj.getType() === 'dropdown' &&
        obj.dataSource !== undefined &&
        obj.dataSource !== '',
    });

    // Add onChangeUpdateQuestion property
    Serializer.addProperty('question', {
      name: 'onChangeUpdateQuestion',
      type: 'string',
      category: 'RNG Settings',
      visibleIndex: 4,
      default: '',
      visibleIf: (obj) => true,
    });

    // Add renderAs property (following dataSource pattern)
    Serializer.addProperty('question', {
      name: 'rendersAs',
      type: 'string',
      category: 'RNG Settings',
      visibleIndex: 5,
      default: 'default',
      choices: ['default', 'autocomplete', 'multiselect'],
      visibleIf: (obj) => obj.getType() === 'dropdown',
    });

    // Add datetime constraint properties for datetime-local inputs
    Serializer.addProperty('question', {
      name: 'defaultDateTime',
      displayName: 'Default date/time',
      type: 'string',
      category: 'RNG Settings',
      visibleIndex: 6,
      visibleIf: (obj) =>
        obj.getType() === 'text' && obj.inputType === 'datetime-local',
    });

    Serializer.addProperty('question', {
      name: 'datetimeMaxConstraintEnabled',
      displayName: 'Enable Max Date/Time Constraint',
      type: 'boolean',
      category: 'RNG Settings',
      visibleIndex: 7,
      default: false,
      visibleIf: (obj) =>
        obj.getType() === 'text' && obj.inputType === 'datetime-local',
    });

    Serializer.addProperty('question', {
      name: 'datetimeMaxConstraintQuestion',
      displayName: 'Max Date/Time Source Question',
      type: 'dropdown',
      category: 'RNG Settings',
      visibleIndex: 8,
      default: '',
      choices: (obj: any) => {
        // Get all datetime questions from the survey
        if (obj && obj.survey) {
          const datetimeQuestions = obj.survey.getAllQuestions().filter(
            (q: any) =>
              q.getType() === 'text' &&
              q.inputType === 'datetime-local' &&
              q.name !== obj.name // Don't include self
          );
          return datetimeQuestions.map((q: any) => ({
            value: q.name,
            text: q.title || q.name,
          }));
        }
        return [];
      },
      visibleIf: (obj) =>
        obj.getType() === 'text' &&
        obj.inputType === 'datetime-local' &&
        obj.datetimeMaxConstraintEnabled === true,
    });

    Serializer.addProperty('question', {
      name: 'datetimeShowCopyButton',
      displayName: 'Show Copy Button',
      type: 'boolean',
      category: 'RNG Settings',
      visibleIndex: 9,
      default: true,
      visibleIf: (obj) =>
        obj.getType() === 'text' &&
        obj.inputType === 'datetime-local' &&
        obj.datetimeMaxConstraintEnabled === true,
    });

    // Add authVisibility property to pages
    // This controls who can see the page based on authentication status:
    // 0 = Everyone (default)
    // 1 = Only logged in users
    // 2 = Only NOT logged in users
    // 3 = Nobody (page is hidden)
    Serializer.addProperty('page', {
      name: 'authVisibility',
      displayName: 'Page Visibility (Auth)',
      type: 'dropdown',
      category: 'general',
      visibleIndex: 1,
      choices: [
        { value: 0, text: 'Everyone' },
        { value: 1, text: 'Only logged in' },
        { value: 2, text: 'NOT logged in' },
        { value: 3, text: 'Nobody' },
      ],
      default: 0,
    });

    // Set up onPropertyChanged handler for questions
    this.surveyCreator.onPropertyChanged.add((sender: Base, options: any) => {
      const obj = sender as Question;
      if (
        (options.name === 'dataSource' ||
          options.name === 'dataSourceKeyField' ||
          options.name === 'dataSourceValueField') &&
        obj.getType() === 'dropdown'
      ) {
        const dataSource = obj.getPropertyValue('dataSource');
        if (dataSource) {
          const result =
            this.componentDataSourceService.getDataSource(dataSource);
          if (result instanceof Observable) {
            result.subscribe((data: any[]) => {
              if (Array.isArray(data) && data.length > 0) {
                const valueField =
                  obj.getPropertyValue('dataSourceKeyField') || 'id';
                const textField =
                  obj.getPropertyValue('dataSourceValueField') || 'name';
                obj['choices'] = data.map((item) => {
                  const choice = {
                    value: item[valueField],
                    text: item[textField],
                  };
                  return choice;
                });
              }
            });
          }
        }
      }
    });

    // Configure tagbox settings for the creator
    this.surveyCreator.toolbox.addItem({
      name: 'tagbox',
      title: 'Tag Box',
      category: 'Form Elements',
      json: {
        type: 'tagbox',
        name: 'tags',
        title: 'Select or add tags',
        choices: ['tag1', 'tag2', 'tag3'],
        hasOther: true,
        allowAddTags: true,
        placeholder: 'Choose or type tags...',
      },
    });

    // Add dual panel selector to toolbox
    try {
      this.surveyCreator.toolbox.addItem({
        name: 'dualpanel',
        title: 'Dual Panel Selector',
        category: 'Custom Questions',
        isCopied: false,
        iconName: 'icon-panel',
        json: {
          type: 'dualpanel',
          name: 'dualPanelSelector',
          title: 'Select Items',
        },
      });
    } catch (error) {
      console.error(
        '[EditComponentModal] Error adding dual panel selector to toolbox:',
        error
      );
    }

    // Add dynamic table to toolbox
    try {
      this.surveyCreator.toolbox.addItem({
        name: 'dynamictable',
        title: 'Dynamic Table',
        category: 'Custom Questions',
        isCopied: false,
        iconName: 'icon-table',
        json: {
          type: 'dynamictable',
          name: 'dynamicTable',
          title: 'Data Table',
          description: 'A dynamic data table component',
          dataSource: 'test_data', // Default data source
          enableSearch: true, // Enable search by default
          pageSize: 10, // Default page size
          striped: true, // Enable striped rows
          rowHover: true, // Enable row hover effect
          defaultSortColumn: '', // Empty default sort column
          defaultSortDirection: 'asc', // Default sort direction
          dense: false, // Default density
          multiSelect: false, // Default selection mode
          filters: '[]', // Default empty filters array
        },
      });
    } catch (error) {
      console.error(
        '[EditComponentModal] Error adding dynamic table to toolbox:',
        error
      );
    }

    // Add translations panel to toolbox
    try {
      this.surveyCreator.toolbox.addItem({
        name: translationsPanel.name,
        title: translationsPanel.title,
        category: translationsPanel.category,
        json: translationsPanel.json,
      });
    } catch (error) {
      console.error(
        '[EditComponentModal] Error adding tabbedpanel to toolbox:',
        error
      );
    }

    // Configure default settings for questions
    this.surveyCreator.onQuestionAdded.add((sender, options) => {
      if (options.question.getType() === 'tagbox') {
        options.question['searchEnabled'] = true;
        options.question['allowAddTags'] = true;
        options.question['placeholder'] = 'Choose or type tags...';
      }
      if (
        options.question.getType() === 'panel' &&
        options.question.name === 'dualPanelSelector'
      ) {
        dualPanelSelector.afterRender(
          options.question,
          options.question['rootElement']
        );
      }
      if (options.question.getType() === 'dynamictable') {
        dynamicTableQuestion.afterRender(
          options.question,
          options.question['rootElement']
        );
      }
    });

    if (this.component?.inputs) {
      Object.entries(this.component.inputs).forEach(([key, value]) => {
      });
    }

    // Initialize the survey creator with the survey JSON (or read-only bundled form from repo)
    const bundledFormId = getBundledSurveyFormIdFromComponentInputs(
      this.component?.inputs
    );
    if (bundledFormId) {
      const bundled = resolveBundledSurveyForm(bundledFormId);
      this.surveyCreator.text = JSON.stringify(bundled ?? {}, null, 2);
      this.surveyCreator.readOnly = true;
    } else {
      this.surveyCreator.readOnly = false;
      const surveyJSON = this.component?.inputs?.['surveyJSON']?.value;
      if (surveyJSON) {
        this.surveyCreator.text =
          typeof surveyJSON === 'string'
            ? surveyJSON
            : JSON.stringify(surveyJSON);
      } else {
        this.surveyCreator.text = '{}';
      }
    }
    // Ensure the survey model has showEmptyFormSubmit set to true by default when missing
    this.surveyCreator.survey?.setPropertyValue(
      'showEmptyFormSubmit',
      this.surveyCreator.survey?.getPropertyValue('showEmptyFormSubmit') ?? true
    );

    // Set form-level data source properties from component settings if available
    if (this.component?.name === 'dynamic-form' && this.surveyCreator.survey) {
      const dataSource = this.component.settings['formDataSource']?.value;
      const dataSourceKeyField =
        this.component.settings['formDataSourceKeyField']?.value;
      const dataSourceValueField =
        this.component.settings['formDataSourceValueField']?.value;

      // If this is an event edit form, set the data source to event_form_data
      if (
        this.component.inputs['surveyJSON']?.value &&
        typeof this.component.inputs['surveyJSON'].value === 'string' &&
        this.component.inputs['surveyJSON'].value.includes(
          '"title": "Rediger Event"'
        )
      ) {
        this.surveyCreator.survey.setPropertyValue(
          'dataSource',
          'event_form_data'
        );
      } else if (dataSource) {
        this.surveyCreator.survey.setPropertyValue('dataSource', dataSource);
      }

      if (dataSourceKeyField) {
        this.surveyCreator.survey.setPropertyValue(
          'dataSourceKeyField',
          dataSourceKeyField
        );
      }

      if (dataSourceValueField) {
        this.surveyCreator.survey.setPropertyValue(
          'dataSourceValueField',
          dataSourceValueField
        );
      }

    }

    // Set up auto-save
    this.surveyCreator.saveSurveyFunc = (
      saveNo: number,
      callback: Function
    ) => {
      if (
        this.component &&
        this.component.inputs['surveyJSON'] &&
        !getBundledSurveyFormIdFromComponentInputs(this.component.inputs)
      ) {
        const persisted = this.persistShowEmptyFormSubmit(
          this.surveyCreator.text
        );
        this.component.inputs['surveyJSON'].value = persisted;
        this.surveyCreator.text = persisted; // keep creator text in sync
      }
      callback(saveNo, true);
    };

    // Subscribe to function registry updates
    this.functionRegistry.functionNames$
      .pipe(takeUntil(this.destroy$))
      .subscribe((names) => {
        if (this.component?.inputs?.['clickFunction']) {
          this.component.inputs['clickFunction'].options = names;
          this.changeDetector.detectChanges();
        }
      });

    // Load saved roles if they exist
    if (this.component?.settings['allowedRoles']?.value) {
      this.selectedRoles = [...this.component.settings['allowedRoles'].value];
      this.availableRoles = this.availableRoles.filter(
        (role) => !this.selectedRoles.includes(role)
      );
    }

    // Initialize permission settings
    this.availablePermissions =
      this.componentPermissionService.getRegisteredPermissions();

    // Load saved permission settings if they exist
    if (this.component?.settings['permissionFunction']?.value) {
      this.selectedPermissionFunction =
        this.component.settings['permissionFunction'].value;
    }
    if (this.component?.settings['permissionContextFields']?.value) {
      this.permissionContextFields =
        this.component.settings['permissionContextFields'].value;
    }
    if (
      this.component?.settings['enablePermissionCheck']?.value !== undefined
    ) {
      this.enablePermissionCheck =
        this.component.settings['enablePermissionCheck'].value;
    }
  }

  insertComponent() {
    if (!this.editor) {
      console.error('Editor not initialized');
      return;
    }

    const shortcode = this.componentLibrary.generateShortcode(
      this.selectedComponent,
      this.componentProperties
    );

    // Insert the shortcode at current cursor position
    this.editor.model.change((writer: any) => {
      const insertPosition =
        this.editor.model.document.selection.getFirstPosition();
      writer.insertText(shortcode, insertPosition);
    });

    // Clear the selection after insert
    this.selectedComponent = '';
    this.componentProperties = {};
  }

  isHTMLTextEditor(): boolean {
    const inputs = this.component?.getOrSetInputs();
    if (!inputs) return false;

    return inputs['content']?.type === 'HTMLText';
  }

  getSelectedComponentEvents(): string[] {
    const component = this.availableComponents.find(
      (c) => c.name === this.selectedComponent
    );
    return component?.properties.events || [];
  }

  addCssShortcut() {
    if (!this.selectedCssShortcut) return;

    if (!this.component) return;

    const currentCSS = this.component.customCSS || '';
    const newCSS =
      currentCSS +
      (currentCSS && !currentCSS.endsWith('\n') ? '\n' : '') +
      this.selectedCssShortcut +
      '\n';

    this.component.customCSS = newCSS;
    this.selectedCssShortcut = ''; // Reset selection

  }

  dropRole(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
  }

  moveRolesTo(from: string[], to: string[]) {
    to.push(...from);
    from.length = 0;
  }

  capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  toSelectOptions(
    values: readonly string[] | undefined,
    placeholder?: string
  ): NativeSelectOption[] {
    const options = (values ?? []).map((value) => ({ value, label: value }));
    return placeholder
      ? [{ value: '', label: placeholder }, ...options]
      : options;
  }

  componentInputEntries(): {
    key: string;
    value: {
      type: string;
      value: any;
      options?: string[];
      displayText?: string;
      description?: string;
    };
  }[] {
    const inputs = this.component?.getOrSetInputs();
    if (!inputs) {
      return [];
    }
    return Object.entries(inputs).map(([key, value]) => ({ key, value }));
  }

  permissionSelectOptions(): NativeSelectOption[] {
    return [
      { value: '', label: 'Select a permission function...' },
      ...this.availablePermissions.map((permission) => ({
        value: permission,
        label: `${permission} - ${this.getPermissionDescription(permission)}`,
      })),
    ];
  }

  nativeInputValue(event: Event): string {
    return (event.target as HTMLInputElement | HTMLTextAreaElement).value;
  }

  nativeInputNumber(event: Event): number {
    return (event.target as HTMLInputElement).valueAsNumber;
  }

  nativeCheckboxChecked(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  initializeComponentSettings() {
    if (this.component) {
      // Ensure settings object exists (handle both undefined and null)
      // Create a new object reference to ensure Angular change detection picks it up
      const needsNewSettings =
        !this.component.settings || this.component.settings === null;
      if (needsNewSettings) {
        this.component.settings = {};
      }

      // Initialize common settings that are used in the template
      // Check both for existence and if value is undefined/null
      const needsCustomID =
        !this.component.settings['customID'] ||
        this.component.settings['customID'] === null;
      if (needsCustomID) {
        this.component.settings['customID'] = {
          type: 'string',
          value: `${this.component.name}_${
            this.component.uuid || 'new'
          }_parent`,
        };
      } else if (
        this.component.settings['customID'].value === undefined ||
        this.component.settings['customID'].value === null
      ) {
        // Ensure value exists even if the setting object exists
        this.component.settings['customID'].value = `${this.component.name}_${
          this.component.uuid || 'new'
        }_parent`;
      }

      const needsCustomClasses =
        !this.component.settings['customClasses'] ||
        this.component.settings['customClasses'] === null;
      if (needsCustomClasses) {
        this.component.settings['customClasses'] = {
          type: 'string',
          value: '',
        };
      } else if (
        this.component.settings['customClasses'].value === undefined ||
        this.component.settings['customClasses'].value === null
      ) {
        // Ensure value exists even if the setting object exists
        this.component.settings['customClasses'].value = '';
      }

      // Trigger change detection after initialization
      this.changeDetector.markForCheck();

      // Initialize autoHeight property for all components if it doesn't exist
      if (!this.component.inputs) {
        this.component.inputs = {};
      }

      if (!this.component.inputs['autoHeight']) {
        this.component.inputs['autoHeight'] = {
          type: 'boolean',
          value: true,
        };
      }

      // Initialize component-specific inputs based on component type
      this.initializeComponentInputs();

      // Initialize DynamicTableComponent specific settings
      if (this.component.name === 'DynamicTableComponent') {
        // Initialize sort settings with proper type and default values
        if (!this.component.settings['defaultSortColumn']) {
          this.component.settings['defaultSortColumn'] = {
            type: 'text',
            value: '',
          };
        }

        if (!this.component.settings['defaultSortDirection']) {
          this.component.settings['defaultSortDirection'] = {
            type: 'select',
            value: 'asc',
          };
        }

        // Ensure existing values are valid
        if (
          this.component.settings['defaultSortColumn'].value === undefined ||
          this.component.settings['defaultSortColumn'].value === null
        ) {
          this.component.settings['defaultSortColumn'].value = '';
        }

        if (
          this.component.settings['defaultSortDirection'].value === undefined ||
          this.component.settings['defaultSortDirection'].value === null ||
          !['asc', 'desc'].includes(
            this.component.settings['defaultSortDirection'].value
          )
        ) {
          this.component.settings['defaultSortDirection'].value = 'asc';
        }

        if (!this.component.settings['focusSearchOnLoad']) {
          this.component.settings['focusSearchOnLoad'] = {
            type: 'boolean',
            value: false,
          };
        }
        if (
          this.component.settings['focusSearchOnLoad'].value === undefined ||
          this.component.settings['focusSearchOnLoad'].value === null
        ) {
          this.component.settings['focusSearchOnLoad'].value = false;
        }

      }

    }
  }

  initializeComponentInputs() {
    if (!this.component || !this.component.inputs) {
      return;
    }

    // Initialize DynamicTabsComponent specific inputs (structured tab sets with rules)
    if (this.component.name === 'DynamicTabsComponent') {
      if (!this.component.inputs['tabsConfig']) {
        this.component.inputs['tabsConfig'] = {
          type: 'object',
          value: {
            version: 1,
            rules: [{ condition: { auth: 'any' }, tabs: [], isDefault: true }],
          },
          displayText: 'Tab sets (conditional menus)',
        };
      }
    }

    // Add other component-specific input initializations here as needed
  }

  // Method to handle autoHeight changes
  onAutoHeightChange(value: boolean) {
    if (this.component && this.component.inputs) {
      this.component.inputs['autoHeight'].value = value;
    }
  }

  getColumnNames(): Array<{ key: string; header?: string }> {
    try {
      if (this.component && this.component.name === 'DynamicTableComponent') {
        // Try to get columns from inputs if they exist
        if (
          this.component.inputs &&
          this.component.inputs['columns'] &&
          this.component.inputs['columns'].value
        ) {
          let columnsInput = this.component.inputs['columns'].value;

          // If it's a JSON string, parse it
          if (typeof columnsInput === 'string') {
            try {
              return JSON.parse(columnsInput);
            } catch (e) {
            }
          }

          // If it's already an array, use it
          if (Array.isArray(columnsInput)) {
            return columnsInput;
          }
        }

        // If no columns found, return a few common column names as fallback
        return [
          { key: 'id', header: 'ID' },
          { key: 'name', header: 'Name' },
          { key: 'title', header: 'Title' },
          { key: 'description', header: 'Description' },
          { key: 'date', header: 'Date' },
          { key: 'status', header: 'Status' },
        ];
      }
    } catch (e) {
      console.error('Error in getColumnNames:', e);
    }
    return [];
  }

  updateSortColumn(value: string) {
    if (this.component && this.component.settings) {
      if (!this.component.settings['defaultSortColumn']) {
        this.component.settings['defaultSortColumn'] = {
          type: 'text',
          value: value,
        };
      } else {
        this.component.settings['defaultSortColumn'].value = value;
      }
    }
  }

  updateSortDirection(value: string) {
    if (this.component && this.component.settings) {
      if (!this.component.settings['defaultSortDirection']) {
        this.component.settings['defaultSortDirection'] = {
          type: 'select',
          value: value,
        };
      } else {
        this.component.settings['defaultSortDirection'].value = value;
      }
    }
  }

  getPermissionDescription(permissionKey: string): string {
    return (
      this.componentPermissionService.getPermissionDescription(permissionKey) ||
      'No description available'
    );
  }

  requiresPermissionContext(permissionKey: string): boolean {
    return this.componentPermissionService.requiresContext(permissionKey);
  }

  // --- Dynamic Tabs: parse/serialize and rule/tab editing ---

  /** Parse tabsConfig input value (string or object) into editor rules. */
  parseTabsConfigToRules(value: any): TabSetRuleEdit[] {
    this.alwaysVisibleTabs = [];
    if (value == null) return [];
    if (typeof value === 'string') {
      const s = value.trim();
      if (!s) return [];
      const tabs: TabConfigEdit[] = s
        .split(';')
        .map((part) => {
          const [text, slug] = part.split(',');
          return { text: (text || '').trim(), slug: (slug || '').trim() };
        })
        .filter((t) => t.text && t.slug);
      return [{ condition: { auth: 'any' }, tabs, isDefault: true }];
    }
    if (
      typeof value === 'object' &&
      value.rules &&
      Array.isArray(value.rules)
    ) {
      this.alwaysVisibleTabs = Array.isArray(value.alwaysVisibleTabs)
        ? value.alwaysVisibleTabs.map((t: any) => ({
            text: t.text || '',
            slug: t.slug || '',
            dataSource: t.dataSource || '',
          }))
        : [];
      return value.rules.map((r: any) => ({
        condition: {
          auth: r.condition?.auth || 'any',
          ...(Array.isArray(r.condition?.roles) && r.condition.roles.length
            ? { roles: [...r.condition.roles] }
            : {}),
          ...(typeof r.condition?.dataSource === 'string' &&
          r.condition.dataSource.trim()
            ? { dataSource: r.condition.dataSource.trim() }
            : {}),
        },
        tabs: Array.isArray(r.tabs)
          ? r.tabs.map((t: any) => ({ text: t.text || '', slug: t.slug || '' }))
          : [],
        isDefault: !!r.isDefault,
      }));
    }
    return [];
  }

  /** Serialize editor rules to tabsConfig value (object). */
  serializeRulesToTabsConfig(
    rules: TabSetRuleEdit[],
    alwaysVisible: AlwaysVisibleTabEdit[]
  ): TabsConfigRules {
    const config: TabsConfigRules = {
      version: 1,
      rules: rules.map((r) => ({
        condition: this.normalizeTabSetCondition(r.condition),
        tabs: r.tabs.map((t) => ({ text: t.text, slug: t.slug })),
        isDefault: r.isDefault,
      })),
    };
    const filteredAlwaysVisible = alwaysVisible
      .filter((t) => t.text?.trim() && t.slug?.trim() && t.dataSource?.trim())
      .map((t) => ({
        text: t.text.trim(),
        slug: t.slug.trim(),
        dataSource: t.dataSource.trim(),
      }));
    if (filteredAlwaysVisible.length > 0) {
      config.alwaysVisibleTabs = filteredAlwaysVisible;
    }
    return config;
  }

  isTabSetDataSourceAuth(auth: TabSetCondition['auth']): boolean {
    return auth === 'data-source-true' || auth === 'data-source-false';
  }

  onTabSetAuthChange(rule: TabSetRuleEdit): void {
    if (!this.isTabSetDataSourceAuth(rule.condition.auth)) {
      delete rule.condition.dataSource;
    }
    this.changeDetector.markForCheck();
  }

  onTabSetAuthSelected(rule: TabSetRuleEdit, auth: string): void {
    rule.condition.auth = auth as TabSetCondition['auth'];
    this.onTabSetAuthChange(rule);
  }

  private normalizeTabSetCondition(
    condition: TabSetCondition
  ): TabSetCondition {
    const normalized: TabSetCondition = { auth: condition.auth || 'any' };
    if (Array.isArray(condition.roles) && condition.roles.length > 0) {
      normalized.roles = [...condition.roles];
    }
    if (
      this.isTabSetDataSourceAuth(normalized.auth) &&
      condition.dataSource?.trim()
    ) {
      normalized.dataSource = condition.dataSource.trim();
    }
    return normalized;
  }

  ensureTabSetRulesFromComponent(): void {
    if (
      !this.component ||
      this.component.name !== 'DynamicTabsComponent' ||
      !this.component.inputs['tabsConfig']
    )
      return;
    const raw = this.component.inputs['tabsConfig'].value;
    this.tabSetRules = this.parseTabsConfigToRules(raw);
    if (this.tabSetRules.length === 0) {
      this.tabSetRules = [
        { condition: { auth: 'any' }, tabs: [], isDefault: true },
      ];
    }
    if (!Array.isArray(this.alwaysVisibleTabs)) {
      this.alwaysVisibleTabs = [];
    }
  }

  addAlwaysVisibleTab(): void {
    this.alwaysVisibleTabs.push({ text: '', slug: '', dataSource: '' });
    this.changeDetector.markForCheck();
  }

  removeAlwaysVisibleTab(index: number): void {
    this.alwaysVisibleTabs.splice(index, 1);
    this.changeDetector.markForCheck();
  }

  moveAlwaysVisibleTab(from: number, to: number): void {
    if (to < 0 || to >= this.alwaysVisibleTabs.length) return;
    const item = this.alwaysVisibleTabs.splice(from, 1)[0];
    this.alwaysVisibleTabs.splice(to, 0, item);
    this.changeDetector.markForCheck();
  }

  addTabSetRule(): void {
    this.tabSetRules.push({
      condition: { auth: 'any' },
      tabs: [],
      isDefault: false,
    });
    this.changeDetector.markForCheck();
  }

  removeTabSetRule(index: number): void {
    this.tabSetRules.splice(index, 1);
    this.changeDetector.markForCheck();
  }

  setRuleDefault(index: number, isDefault: boolean): void {
    if (isDefault) {
      this.tabSetRules.forEach((r, i) => {
        r.isDefault = i === index;
      });
    } else {
      this.tabSetRules[index].isDefault = false;
    }
    this.changeDetector.markForCheck();
  }

  addTabToRule(ruleIndex: number): void {
    const rule = this.tabSetRules[ruleIndex];
    if (!rule) return;
    rule.tabs = rule.tabs || [];
    rule.tabs.push({ text: '', slug: '' });
    this.changeDetector.markForCheck();
  }

  removeTabFromRule(ruleIndex: number, tabIndex: number): void {
    const rule = this.tabSetRules[ruleIndex];
    if (!rule || !rule.tabs) return;
    rule.tabs.splice(tabIndex, 1);
    this.changeDetector.markForCheck();
  }

  moveTabInRule(ruleIndex: number, fromIndex: number, toIndex: number): void {
    const rule = this.tabSetRules[ruleIndex];
    if (
      !rule ||
      !rule.tabs ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= rule.tabs.length ||
      toIndex >= rule.tabs.length
    )
      return;
    const [item] = rule.tabs.splice(fromIndex, 1);
    rule.tabs.splice(toIndex, 0, item);
    this.changeDetector.markForCheck();
  }

  getRolesForRule(rule: TabSetRuleEdit): string[] {
    return rule.condition.roles || [];
  }

  setRolesForRule(ruleIndex: number, roles: string[]): void {
    this.tabSetRules[ruleIndex].condition.roles = roles.length
      ? roles
      : undefined;
    this.changeDetector.markForCheck();
  }

  toggleRuleRole(ruleIndex: number, role: string): void {
    const rule = this.tabSetRules[ruleIndex];
    if (!rule) return;
    const current = rule.condition.roles || [];
    const has = current.includes(role);
    rule.condition.roles = has
      ? current.filter((r) => r !== role)
      : [...current, role];
    if (rule.condition.roles.length === 0) rule.condition.roles = undefined;
    this.changeDetector.markForCheck();
  }

  /**
   * Ensure showEmptyFormSubmit is retained in survey JSON (especially when false/true)
   * Arrow function keeps `this` bound and satisfies TS property lookup.
   */
  persistShowEmptyFormSubmit = (json: any, explicitValue?: boolean): string => {
    try {
      const parsed =
        typeof json === 'string' ? JSON.parse(json || '{}') : json || {};
      const showEmptyFormSubmit =
        explicitValue !== undefined
          ? explicitValue
          : this.surveyCreator?.survey?.getPropertyValue(
              'showEmptyFormSubmit'
            ) ?? true;

      if (showEmptyFormSubmit !== undefined) {
        parsed.showEmptyFormSubmit = showEmptyFormSubmit;
      }

      return JSON.stringify(parsed);
    } catch (error) {
      console.error(
        '[EditComponentModal] Failed to persist showEmptyFormSubmit:',
        error
      );
      return typeof json === 'string' ? json : JSON.stringify(json || {});
    }
  };
}
